import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ACSApi } from '../../../api/ACSApi';
import { useContainer } from '../../useContainer';
import { ActivityLogWithUsers } from '../../../commonTypes/ActivityLogTypes/activityLog.types';
import { ToastStateHandler } from '../../../api';
import { setCachedActivityLogs, selectCachedActivityLogs } from '../../../store/slices/projects.slice';

const useHttpGetCombinedActivityLogs = (projectId: string) => {
    const acsApi = useContainer(ACSApi);
    const toastHandler = useContainer(ToastStateHandler);
    const dispatch = useDispatch();
    const cachedData = useSelector(selectCachedActivityLogs(projectId));
    const [isReady, setReady] = useState(false);
    const [data, setData] = useState<ActivityLogWithUsers[]>([]);

    const fetchActivityLogs = (forceRefresh = false) => {
        if (!forceRefresh && cachedData) {
            setData(cachedData);
            setReady(true);
            return;
        }

        acsApi
            .getCombinedActivityLogs({
                params: { projectId },
                onSuccess: res => {
                    const transformedData = res.body.map((log: any) => ({
                        ...log,
                        initiatorUser: log.initiatorName
                            ? {
                                  userSub: log.initiatorUserSub || 'unknown',
                                  givenName: log.initiatorName.givenName,
                                  familyName: log.initiatorName.familyName,
                              }
                            : undefined,
                    }));
                    dispatch(setCachedActivityLogs({ projectId, data: transformedData }));
                    setData(transformedData);
                },
                onError: () => toastHandler.push('warn', 'Activity Logs', 'Failed to fetch combined activity logs'),
                onFinally: () => setReady(true),
            })
            .exec();
    };

    useEffect(() => {
        if (cachedData) {
            setData(cachedData);
            setReady(true);
            return;
        }

        fetchActivityLogs();
        return () => acsApi.getCombinedActivityLogs({ params: { projectId } }).abort();
    }, [projectId]);

    return { data, isReady, refetch: fetchActivityLogs };
};

export default useHttpGetCombinedActivityLogs;
